import React from 'react';
import { Dialog } from '@headlessui/react';
import { TagIcon } from '@heroicons/react/24/solid';
import { SimpleModalTW } from '../../../../tailwindComponents/modals/SimpleModalTW';
import { ButtonTW, ColorlessButtonTW } from '../../../../tailwindComponents/buttonTW/ButtonTW';
import { useDeleteOrderTagMutation } from '../../../../API/order/tags/OrderTagApi';
import { Form, Formik } from 'formik';
import { IOrderTagData } from '../../../../types/order/tags/IOrderTagData';
import { IDeleteOrderTagRequest } from '../../../../types/order/tags/ICreateOrderTagRequest';
import { checkForMutationSuccess } from '../../../../API/checkForMutationSuccess';
import { ErrorMessageTW } from '../../../../tailwindComponents/errorMessage/ErrorMessageTW';
import { IHasTestData } from '../../../../ui-toolkit/test-utilities/IHasTestData';
import { mergeTests } from '../../../../ui-toolkit/test-utilities/mergeTests';

interface RemoveOrderTagFormProps extends IHasTestData {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  orderTag: IOrderTagData;
}

export const RemoveOrderTagForm: React.FC<RemoveOrderTagFormProps> = ({
  open,
  setOpen,
  orderTag: {
    orderId,
    tag: { id },
  },
  ...rest
}) => {
  const [deleteOrderTag, { error, isLoading }] = useDeleteOrderTagMutation();

  const handleDelete = async (values: IDeleteOrderTagRequest) => {
    const result = await deleteOrderTag(values);
    if (checkForMutationSuccess(result, 'Order Tag Removal', 'Error Removing Order Tag')) {
      setOpen(false);
    }
  };

  return (
    <SimpleModalTW open={open} setOpen={setOpen}>
      <ErrorMessageTW rtkError={error} />
      <div
        className="sm:flex sm:items-start"
        data-test-id={mergeTests(rest['data-test-id'], 'order-tag-remove-form')}
        data-test-class={mergeTests(rest['data-test-class'], 'order-tag-remove-form')}
      >
        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
          <TagIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
        </div>
        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
          <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
            Remove Tag
          </Dialog.Title>
          <div className="mt-2">
            <p className="text-sm text-gray-500">
              Are you sure you want to remove this tag from this order? This action cannot be undone.
            </p>
          </div>

          <Formik initialValues={{ orderId, tagId: id }} onSubmit={handleDelete}>
            {() => (
              <Form>
                <div className={'gap-4 pt-4 sm:flex sm:flex-row-reverse'}>
                  <ColorlessButtonTW
                    type="submit"
                    className=" bg-red-500 text-white hover:bg-red-600"
                    disabled={isLoading}
                  >
                    Remove
                  </ColorlessButtonTW>
                  <ButtonTW type="button" secondary onClick={() => setOpen(false)}>
                    Don't Remove
                  </ButtonTW>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </SimpleModalTW>
  );
};
